import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  LayoutGrid, 
  RefreshCw, 
  Printer,
  FileText,
  Settings,
  LogOut,
  ChevronDown,
  User,
  Users,
  Share2,
  LogIn
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { useLifely } from '../../context/LifelyContext';

const navItems = [
  { path: '/', label: 'Dashboard', icon: Home },
  { path: '/maps', label: 'Maps', icon: LayoutGrid },
  { path: '/will-status', label: 'Formal Arrangements', icon: FileText },
  { path: '/continuity', label: 'Continuity', icon: RefreshCw },
  { path: '/contacts', label: 'Contacts', icon: Users },
  { path: '/sharing', label: 'Sharing', icon: Share2 },
  { path: '/print-export', label: 'Export', icon: Printer },
];

export const Navigation = () => {
  const location = useLocation();
  const { user, isAuthenticated, logout, login } = useLifely();

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Get display name safely
  const displayName = user?.firstName || user?.fullName?.split(' ')[0] || user?.email?.split('@')[0] || 'User';
  const displayInitial = displayName.charAt(0).toUpperCase();

  const handleSignIn = async () => {
    const memberstack = window.$memberstackDom;
    
    if (!memberstack) {
      console.error('Memberstack not loaded');
      return;
    }

    try {
      // Open the login modal and wait for it to complete
      const result = await memberstack.openModal('LOGIN');
      console.log('Login result:', result);
      
      // After successful login, get current member data
      const memberResult = await memberstack.getCurrentMember();
      
      if (memberResult?.data) {
        const member = memberResult.data;
        console.log('Member logged in:', member.id);
        
        // Login to our backend with member ID
        await login(member.id);
        console.log('Backend login successful');
      }
    } catch (err) {
      // User might have closed the modal
      if (err?.message?.includes('closed') || err?.message?.includes('cancel')) {
        console.log('Login modal was closed');
      } else {
        console.error('Login error:', err);
      }
    }
  };

  const handleSignOut = async () => {
    // Try to use Memberstack logout if available
    if (window.$memberstackDom) {
      try {
        await window.$memberstackDom.logout();
      } catch (err) {
        console.error('Memberstack logout error:', err);
      }
    }
    logout();
  };

  return (
    <nav 
      className="sticky top-0 z-50 glass border-b border-border"
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo / Home Link */}
          <div className="flex items-center gap-8">
            <a 
              href="https://lifely.guide" 
              target="_blank" 
              rel="noopener noreferrer"
              className="font-heading text-xl font-semibold text-foreground hover:text-primary transition-colors duration-200"
              data-testid="logo-link"
            >
              Lifely
            </a>

            {/* Main Navigation - only show when authenticated */}
            {isAuthenticated && (
              <div className="hidden md:flex items-center gap-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`
                        flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium
                        transition-colors duration-200
                        ${active 
                          ? 'bg-primary/10 text-primary' 
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }
                      `}
                    >
                      <Icon className="w-4 h-4" />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <>
                <span className="hidden sm:block text-sm text-muted-foreground">
                  Welcome, <span data-ms-member="first-name" className="text-foreground font-medium">{displayName}</span>
                </span>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="flex items-center gap-2 px-2"
                      data-testid="user-menu-trigger"
                    >
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className="bg-primary/10 text-primary text-sm">
                          {displayInitial}
                        </AvatarFallback>
                      </Avatar>
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link 
                        to="/settings" 
                        className="flex items-center gap-2 cursor-pointer"
                        data-testid="settings-menu-item"
                      >
                        <Settings className="w-4 h-4" />
                        Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link 
                        to="/settings#privacy" 
                        className="flex items-center gap-2 cursor-pointer"
                        data-testid="privacy-menu-item"
                      >
                        <User className="w-4 h-4" />
                        Privacy
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      className="flex items-center gap-2 cursor-pointer text-muted-foreground"
                      data-testid="sign-out-menu-item"
                      onClick={handleSignOut}
                    >
                      <LogOut className="w-4 h-4" />
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSignIn}
                data-testid="sign-in-btn"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Sign In
              </Button>
            )}
          </div>
        </div>

        {/* Mobile Navigation - only show when authenticated */}
        {isAuthenticated && (
          <div className="md:hidden flex items-center gap-1 pb-3 overflow-x-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-mobile-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`
                    flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap
                    transition-colors duration-200
                    ${active 
                      ? 'bg-primary/10 text-primary' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }
                  `}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {item.label}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
